/*
 * The ServiceTableRow component encapsulates display of a service in a table row.  It is intended for use
 * in the SO flows.
 * 
 * This takes the following props:
 *   service                 - the subscription object
 *   styledTableRow          - function wrapper for emitting a styled table row
 *   styledTableCell         - function wrapper for emitting a styled table cell
 *   confirmDelete           - parent function for subscription deletion (optional)
 *   editable                - if true, show edit / delete buttons (if actions are provided)
 *   soContext               - resellerSetup or customerView
 */

import React from 'react';
import PropTypes from 'prop-types';
import { red } from '@mui/material/colors';

import { Button, Grid, Tooltip, Typography } from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import AppLogo from '../ui/AppLogo';
import { getDayLabel } from '../../utils/InputUtils';

class ServiceTableRow extends React.Component {

  getBillingDayLabel(billingDayOfMonth) {
        if (billingDayOfMonth === 0 || billingDayOfMonth === null) { 
            return 'Billed on day after order accepted'; 
        } 
        if (billingDayOfMonth === 32) { 
            return 'Billed on last day of month'; 
        } 

        return 'Billed on ' + getDayLabel(billingDayOfMonth) + ' of the month';
    }    
    
    render() {
        const service = this.props.service;
        const StyledTableRow  = this.props.styledTableRow;
        const StyledTableCell = this.props.styledTableCell;

        let viewContext = 'resellerSetup';
        if (this.props.soContext === 'customerView') {
          viewContext = 'customerView';
        }

        let theAmount = service.appSkuPrice;
        let billingAmount = Number(theAmount).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
        let termsDescription = billingAmount;

        
        let billingFrequency = 'One-time fee';
        let billingDayOfMonth = '';
        if (service.billingFrequency === 1) {
          billingFrequency = 'Monthly';
          billingDayOfMonth = this.getBillingDayLabel(service.billingDayOfMonth)
          termsDescription = termsDescription + ' billed monthly.';
        } else {
          termsDescription = termsDescription + ' billed upon approval of this order.';
        }

        let pendingTermsTooltip = 'Remove pending service';
        const cancelColor = red[300];

        let editable = false;
        if (this.props.editable === true && this.props.confirmDelete !== undefined) {
          editable = true;
        }


        if (viewContext === 'customerView') {
          return (
            <StyledTableRow key={service.customerAppId}>
             <StyledTableCell>
                <Grid container alignItems="center" spacing={1}>
                  <Grid item >
                    <AppLogo logoPath={service.appTByAppId.appLogo} appName={service.appTByAppId.appName} imageHeight='24px'/>
                  </Grid>
                  <Grid item>
                    <Typography>{service.appTByAppId.appName}</Typography>
                  </Grid>                            
                </Grid>
              </StyledTableCell>
              <StyledTableCell>{termsDescription}</StyledTableCell>
            </StyledTableRow>
          );
        }

        return (
          <StyledTableRow key={service.customerAppId}>
            <StyledTableCell style={{width: 24}}>
              {service.soAccepted === 0 &&
              <Tooltip placement="right" title="New">
                <Add/>
              </Tooltip>}
            </StyledTableCell>
            <StyledTableCell>
              <Grid container alignItems="center" spacing={1}>
                <Grid item >
                  <AppLogo logoPath={service.appTByAppId.appLogo} appName={service.appTByAppId.appName} imageHeight='24px'/>
                </Grid>
                <Grid item>
                  <Typography>{service.appTByAppId.appName}</Typography>
                </Grid>                            
              </Grid>
            </StyledTableCell>
            <StyledTableCell>{billingAmount}</StyledTableCell>
            <StyledTableCell>{billingFrequency}</StyledTableCell>
            <StyledTableCell>{billingDayOfMonth}</StyledTableCell>
            {editable === true &&
            <StyledTableCell align="right">
                {service.soAccepted === 0 &&
                <Tooltip title={pendingTermsTooltip}>
                    <Button
                    onClick={() => { this.props.confirmDelete(service.customerServiceId);}}>
                    <Delete htmlColor={cancelColor}/>
                    </Button>
                </Tooltip>}
            </StyledTableCell>}
            {editable === false &&
            <StyledTableCell/>}
          </StyledTableRow>
        );    
    }
}

ServiceTableRow.propTypes = {
    service:         PropTypes.object.isRequired,
    styledTableRow:  PropTypes.object.isRequired,
    styledTableCell: PropTypes.object.isRequired,
    editable:        PropTypes.bool.isRequired,
};

export default ServiceTableRow;
